<script setup lang="ts">
import { computed } from 'vue';
import Hint from '@/components/forms/hint/hint.vue';
import type { Emits, Props } from './types';

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(defineProps<Props>(), {
	styling: '',
	rows: 5,
	minHeight: 120,
});

const emit = defineEmits<Emits>();

const localValue = computed({
	get() {
		return props.modelValue;
	},
	set(value) {
		emit('update:modelValue', value);
	},
});
</script>

<template>
	<label
		:class="{
			['top-textarea']: true,
			['top-textarea-' + name]: name,
			['top-disabled']: disabled,
		}"
	>
		<textarea
			v-top-focus.onupdate="isError"
			type="text"
			:class="{
				['top-forms-focusable']: !disabled,
				['top-textarea_textarea']: true,
				['top-textarea_textarea-expandable']: expandable,
				['top-error']: isError,
				[`top-style_${styling}`]: !!styling,
			}"
			autocomplete="off_always"
			:name="name"
			:placeholder="placeholder"
			:disabled="disabled"
			:readonly="readonly"
			:rows="!expandable ? rows : undefined"
			:maxlength="maxLength"
			:="$attrs"
			v-model="localValue"
		/>

		<span
			v-if="expandable"
			class="top-textarea_pseudoContent"
		>
			{{ localValue + ' ' }}
		</span>

		<Hint
			v-if="hint"
			class="top-textarea_hint"
			:hint="hint"
			v-top-tooltip
		/>

		<div
			v-if="maxLength"
			class="top-textarea_counter"
			:class="{
				'top-textarea_counter-max': localValue.length >= maxLength,
			}"
		>
			{{ localValue.length }}/{{ maxLength }}
		</div>
	</label>
</template>

<style>
.top-textarea {
	width: 180px;
	display: inline-flex;
	align-items: center;
	position: relative;
}

.top-textarea_textarea {
	color: var(--color-text-1);
	width: 100%;
	padding: var(--top-forms-padding);
	resize: none;
}

html .top-textarea_textarea.top-textarea_textarea-expandable {
	width: 100%;
	height: calc(100% + var(--top-forms-border-width) * 2);
	position: absolute;
	top: 0;
	left: 0;
}

.top-textarea_pseudoContent {
	pointer-events: none;
	box-sizing: border-box;
	min-height: v-bind(minHeight+ "px");
	max-height: 60vh;
	padding: var(--top-forms-padding);
	font-size: 14px;
	white-space: pre-wrap;
	overflow-wrap: anywhere;
	display: block;
	opacity: 0;
	z-index: -1;
}

.top-textarea_hint {
	height: var(--top-icon-width);
	display: flex;
	position: absolute;
	top: 2px;
	right: 2px;
}

.top-textarea_counter {
	box-shadow: var(--top-forms-background-color) -2px -2px 4px;
	border-radius: 8px;
	background: var(--top-forms-background-color);
	padding: var(--top-padding-1);
	color: var(--color-text-4);
	position: absolute;
	right: var(--top-scrollbar-width, var(--top-forms-border-width));
	bottom: var(--top-forms-border-width);
	z-index: 1;
}

.top-textarea_counter-max {
	color: var(--color-text-negative);
}
</style>
